<script setup lang="ts">
import { cva } from 'class-variance-authority';
import { computed } from 'vue';
import { cn } from '@/helpers/tailwind-helpers';
import BaseLabel from '@/atoms/BaseLabel.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import { displayTypeCva } from '@/atoms/display-type-cva';

export type DisplayType =
  | 'default'
  | 'outline'
  | 'gray'
  | 'brand'
  | 'accent'
  | 'success'
  | 'warning'
  | 'error'
  | 'pink';

export type BaseTagProps = {
  type?: DisplayType;
  size?: 'small' | 'medium';
  class?: string;
  iconName?: string;
  label: string;

  /** NEW */
  clickable?: boolean;
  disabled?: boolean;
};

const baseTag = cva(
  'max-w-min min-w-fit inline-flex items-center justify-center outline-1 -outline-offset-1 rounded-full select-none',
  {
    variants: {
      size: {
        small: 'px-2.5 min-h-7 py-1 space-x-0.5',
        medium: 'px-3 py-1.5 space-x-1',
      },
      type: displayTypeCva,
      clickable: {
        true: 'cursor-pointer hover:opacity-80 transition-opacity',
        false: '',
      },
      disabled: {
        true: 'opacity-40 cursor-not-allowed',
        false: '',
      },
    },
    defaultVariants: {
      type: 'outline',
      size: 'small',
      clickable: false,
      disabled: false,
    },
  },
);

const props = defineProps<BaseTagProps>();

const emit = defineEmits<{
  (e: 'click'): void;
}>();

const classes = computed(() =>
  cn(
    baseTag({
      type: props.type,
      size: props.size,
      clickable: props.clickable && !props.disabled,
      disabled: props.disabled,
    }),
    props.class,
  ),
);

const onClick = () => {
  if (props.disabled) return;
  emit('click');
};
</script>

<template>
  <div :class="classes" @click="onClick">
    <LocalIcon :size="size === 'small' ? 'xs' : 'small'" v-if="iconName" :name="iconName" />
    <BaseLabel :text="label" preset="medium" weight="regular" />
  </div>
</template>
